/**
 * ScoreForecast — „Прогноза на оценката": колко точки или години носи
 * всяка конкретна промяна, пресметнато върху данните на потребителя.
 * Данни: snapshot.forecast (backend compute_forecast).
 */
import React, { useState } from "react";
import { Sparkles, ChevronDown, ChevronUp } from "lucide-react";

export default function ScoreForecast({
  forecast, metric = "protection", isDark = false,
  testid = "score-forecast", limit = 4,
}) {
  const [all, setAll] = useState(false);
  // iter1111 — When the DHI audit retired the numeric forecast, the
  // backend returns `{status: "retired_unvalidated", educational_scenarios}`
  // instead of `{items: [{years_delta, protection_delta, ...}]}`. Rendering
  // the old items format now yields `NaN г.` / `+undefined т.`. Hide the
  // forecast chart entirely when retired — the educational scenarios are
  // shown separately through `ForecastFactorCard` in the DHI page.
  if (!forecast) return null;
  if (forecast._retired || forecast.status === "retired_unvalidated") return null;
  if (!forecast?.items?.length) return null;
  const border = isDark ? "border-white/10" : "border-gray-200";

  const value = (i) => metric === "years" ? -(i.years_delta || 0)
    : metric === "metabolic" ? i.metabolic_delta : i.protection_delta;
  const items = forecast.items
    .filter((i) => value(i) > 0)
    .sort((a, b) => value(b) - value(a));
  if (!items.length) return null;
  const shown = all ? items : items.slice(0, limit);

  const label = (i) => {
    if (metric === "years") {
      return `−${Math.abs(i.years_delta).toFixed(1).replace(".0", "")} г.`;
    }
    return `+${value(i)} т.`;
  };
  const target = (i) => {
    if (metric === "years") return `дентална възраст ${i.dental_age_new}`;
    if (metric === "metabolic") return `оценка ${i.metabolic_new}`;
    return `оценка ${i.protection_new}`;
  };

  return (
    <div className={`mt-4 p-3 rounded-xl border ${border}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1 inline-flex items-center gap-1">
        <Sparkles className="w-3 h-3 text-cyan-500" /> Прогноза — какво печелите от всяка промяна
      </div>
      <p className="text-[10px] opacity-70 mb-2">{forecast.intro_bg}</p>
      <div className="space-y-1.5">
        {shown.map((i) => (
          <div key={i.key} className={`p-2 rounded-lg border ${border} flex items-start justify-between gap-2`}
            data-testid={`${testid}-${i.key}`}>
            <div className="min-w-0">
              <div className="text-[11px] font-bold">{i.label_bg}</div>
              <div className="text-[10px] opacity-70">{i.how_bg}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[13px] font-extrabold text-emerald-500 tabular-nums">
                {label(i)}
              </div>
              <div className="text-[9px] opacity-60">{target(i)}</div>
            </div>
          </div>
        ))}
      </div>
      {items.length > limit && (
        <button onClick={() => setAll(!all)}
          className="mt-2 text-[11px] font-bold text-cyan-500 inline-flex items-center gap-1"
          data-testid={`${testid}-more`}>
          {all ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {all ? "Покажи по-малко" : `Още ${items.length - limit}`}
        </button>
      )}
      <div className="text-[9px] opacity-50 mt-2">{forecast.note_bg}</div>
    </div>
  );
}
